/*
 * robot.js — robot mecha con CHROMA KEY en vivo sobre el intro.
 * ============================================================================
 * Reproduce un <video> con fondo verde (public/video/robot.mp4, mecha caminando/girando en su lugar,
 * loop) y RECORTA el verde en tiempo real con un shader WebGL de chroma key. El resultado —el robot
 * con fondo transparente— se compone en la columna derecha del intro, encima del torrente de glifos.
 *
 * No hay preprocesado (no hace falta ffmpeg): el keying ocurre por-pixel en la GPU. El <video> va
 * muteado + loop + playsinline; su reproducción se desbloquea con el MISMO gesto de "Entrar" que ya
 * habilita el audio (los navegadores no dejan autoplay con sonido sin interacción; muteado sí, pero
 * arrancamos en el gesto por robustez). Paleta y motor de fondo intactos: esto solo AÑADE una capa.
 *
 * El shader también recorta a la CAJA del robot (el clip 1280×720 trae mucho verde alrededor): así el
 * robot llena su columna en vez de quedar diminuto y centrado. La caja se ajusta con CROP_* abajo.
 */

// Caja del robot dentro del cuadro 1280×720, en fracciones con ORIGEN ARRIBA-IZQUIERDA (intuitivo desde
// los frames de referencia). El mecha camina/gira en su lugar → la caja es estable. Generosa para no
// recortar brazos al girar.
// El mecha NO solo gira: BAILA desplazándose por el cuadro (en algunos frames el cuerpo se corre a la
// izquierda y los brazos se abren hasta ~x=0.24). La caja se ensancha para NO cortarlo en el baile; el
// aire extra a la izquierda además separa al robot del texto (columna izquierda).
const CROP_X0 = 0.16, CROP_X1 = 0.66; // horizontal (corrido a la izq del cuadro → robot a la derecha de su caja)
const CROP_YT0 = 0.09, CROP_YT1 = 0.91; // vertical (desde arriba): margen para antenas y pies

const SRC_W = 1280, SRC_H = 720;

/*
 * Monta el robot con chroma key sobre `canvas`. Crea su propio <video> desde `url`.
 * Devuelve { start, dispose } o null si no hay WebGL (degradación limpia: el intro sigue sin robot).
 */
export function mountRobot(canvas, url, opts = {}) {
  if (!canvas) return null;

  // Sonda de contexto: si no hay WebGL, NO instanciamos nada (evita logs de error y degrada limpio).
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: true })
          || canvas.getContext('experimental-webgl', { alpha: true, premultipliedAlpha: false });
  if (!gl) return null;

  // Resolución interna = caja recortada a resolución de la fuente (720p; upsamplear más no añade detalle).
  const iw = Math.round((CROP_X1 - CROP_X0) * SRC_W);
  const ih = Math.round((CROP_YT1 - CROP_YT0) * SRC_H);
  canvas.width = iw;
  canvas.height = ih;

  // <video> fuente (fondo verde). Muteado + loop + inline para autoplay permitido.
  const video = document.createElement('video');
  video.src = url;
  video.loop = true;
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.setAttribute('muted', '');
  video.preload = 'auto';
  video.crossOrigin = 'anonymous'; // mismo origen (/video/…): no queda "tainted"

  // --- programa GLSL: quad de pantalla completa, chroma key + supresión de derrame (spill) ---
  const vs = `
    attribute vec2 aPos;
    attribute vec2 aUv;
    varying vec2 vUv;
    void main() {
      vUv = aUv;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }
  `;
  const fs = `
    precision mediump float;
    uniform sampler2D uTex;
    uniform vec4 uCrop;   // x0, y0(tex, y-arriba), ancho, alto  (en coords de textura ya volteadas)
    uniform vec2 uTexel;  // 1/anchoFuente, 1/altoFuente (para el muestreo del borde)
    uniform float uLow, uHigh, uSpill, uRim;
    varying vec2 vUv;
    // alpha del chroma key en una coord de textura (para detectar el borde de la silueta)
    float keyA(vec2 uv) {
      vec3 c = texture2D(uTex, uv).rgb;
      return 1.0 - smoothstep(uLow, uHigh, c.g - max(c.r, c.b));
    }
    void main() {
      vec2 tUv = vec2(uCrop.x + vUv.x * uCrop.z, uCrop.y + vUv.y * uCrop.w);
      vec3 c = texture2D(uTex, tUv).rgb;
      // "verdor": cuánto excede el verde a los otros canales. Verde puro ~0.5+, robot gris ~0.
      float a = 1.0 - smoothstep(uLow, uHigh, c.g - max(c.r, c.b));
      // supresión de derrame: baja el verde en el fleco para que no quede halo verdoso
      float spill = max(0.0, c.g - max(c.r, c.b));
      c.g -= spill * uSpill;
      if (a <= 0.004) discard;
      // REALCE NEÓN sutil: el rim está donde un vecino es fondo (borde de la silueta). Se conserva el
      // metal del cuerpo; solo el contorno recibe un tinte cromático riso (magenta→cian según x).
      vec2 d = uTexel * 2.0;
      float e = 0.25 * (keyA(tUv + vec2(d.x, 0.0)) + keyA(tUv - vec2(d.x, 0.0))
                      + keyA(tUv + vec2(0.0, d.y)) + keyA(tUv - vec2(0.0, d.y)));
      float rim = smoothstep(0.15, 0.65, a * (1.0 - e));
      vec3 magenta = vec3(1.0, 0.059, 0.62);
      vec3 cyan = vec3(0.0, 0.898, 1.0);
      vec3 neon = mix(magenta, cyan, clamp(vUv.x, 0.0, 1.0));
      c += neon * rim * uRim;
      gl_FragColor = vec4(c, a);
    }
  `;

  function compile(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s);
      return null;
    }
    return s;
  }
  const vShader = compile(gl.VERTEX_SHADER, vs);
  const fShader = compile(gl.FRAGMENT_SHADER, fs);
  if (!vShader || !fShader) return null;
  const prog = gl.createProgram();
  gl.attachShader(prog, vShader);
  gl.attachShader(prog, fShader);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);

  // Quad: dos triángulos cubriendo el clip -1..1; uv 0..1 (uv.y ARRIBA porque volteamos la textura).
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    // aPos     aUv
    -1, -1, 0, 0,
     1, -1, 1, 0,
    -1,  1, 0, 1,
    -1,  1, 0, 1,
     1, -1, 1, 0,
     1,  1, 1, 1,
  ]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(prog, 'aPos');
  const aUv = gl.getAttribLocation(prog, 'aUv');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);

  // Textura del video (se re-sube cada frame). Voltea Y para orientación normal de imagen.
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  // uCrop en coords de textura YA VOLTEADAS (uv.y arriba). En texto y-arriba, el borde inferior de la
  // caja (medida desde arriba con CROP_YT1) es 1 - CROP_YT1; el alto es CROP_YT1 - CROP_YT0.
  gl.uniform1i(gl.getUniformLocation(prog, 'uTex'), 0);
  gl.uniform4f(
    gl.getUniformLocation(prog, 'uCrop'),
    CROP_X0, 1.0 - CROP_YT1, CROP_X1 - CROP_X0, CROP_YT1 - CROP_YT0
  );
  gl.uniform1f(gl.getUniformLocation(prog, 'uLow'), opts.low ?? 0.06);
  gl.uniform1f(gl.getUniformLocation(prog, 'uHigh'), opts.high ?? 0.24);
  gl.uniform1f(gl.getUniformLocation(prog, 'uSpill'), opts.spill ?? 1.0);
  gl.uniform2f(gl.getUniformLocation(prog, 'uTexel'), 1.0 / SRC_W, 1.0 / SRC_H);
  gl.uniform1f(gl.getUniformLocation(prog, 'uRim'), opts.rim ?? 1.2);

  gl.viewport(0, 0, iw, ih);
  gl.clearColor(0, 0, 0, 0);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  let raf = null, running = false, uploaded = false;
  function frame() {
    if (!running) return;
    raf = requestAnimationFrame(frame);
    // Sube el frame vigente solo si el video tiene datos actuales (evita textura negra/errores).
    if (video.readyState >= 2) {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
        uploaded = true;
      } catch (e) { /* algunos navegadores lanzan si el frame no está listo: reintenta el próximo rAF */ }
    }
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (uploaded) gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  return {
    /* Arranca la reproducción (dentro del gesto de "Entrar") y el loop de dibujo. */
    start() {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* sin video: el intro sigue sin robot */ });
      if (!running) { running = true; frame(); }
    },
    /* Detiene el loop, libera GL y el video. */
    dispose() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      try { video.pause(); } catch (e) {}
      video.removeAttribute('src');
      try { video.load(); } catch (e) {}
      gl.deleteTexture(tex);
      gl.deleteBuffer(buf);
      gl.deleteProgram(prog);
      gl.deleteShader(vShader);
      gl.deleteShader(fShader);
    },
  };
}
